import classNames from 'classnames';
import { useAuth } from '@/contexts';
import { Button } from '@/components/Button/ButtonV2';
import { Card, CardContent } from '@/components/Card/Card';
import { useDownloadSlide } from '@/containers/Posts/useDownloadSlide';
import { faEye } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useClient } from '@/hooks';
import { VendorI } from '@/types/cyclone/models';
import { toast } from 'react-toastify';
import { InputV2 } from '@/components/Input';
import { Loader } from '@/components';

export enum TabType {
  FEED = 'feed',
  STORY = 'story'
}

export default function Posts() {
  const { session } = useAuth();
  const { client } = useClient();
  const [activeTab, setActiveTab] = useState<TabType>(TabType.FEED);
  const [selectedVendor, setSelectedVendor] = useState<string>(session?.vendor?.username as string);
  const [usernameInput, setUsernameInput] = useState<string>('');

  const FLASH_URL = import.meta.env.VITE_FLASH_URL || 'https://pro.agora.red';
  const [downloadingSlides, setDownloadingSlides] = useState<number[]>([]);
  const { isLoading, mutate: downloadSlide } = useDownloadSlide(
    setDownloadingSlides,
    selectedVendor,
    activeTab
  );

  const { data: vendors, isLoading: isLoadingVendors } = useQuery(
    ['vendors', 'published'],
    async () =>
      await client<VendorI[]>('vendors/published', 'GET', {
        isAuthRequired: true
      }),
    {
      retry: false,
      refetchOnWindowFocus: false
    }
  );
  const validVendors = useMemo(() => {
    return vendors?.filter((vendor) => vendor && vendor.username && vendor.id) || [];
  }, [vendors]);

  const sortedVendors = useMemo(() => {
    return [...validVendors].sort((a, b) => a.username.localeCompare(b.username));
  }, [validVendors]);

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    toast.success(`Ahora estás viendo la cuenta de ${event.target.value}`);
    setSelectedVendor(event.target.value);
    setUsernameInput('');
  };

  const handleUsernameInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUsernameInput(event.target.value);
  };

  const handleSubmit = async () => {
    let vendor: VendorI | undefined;

    if (usernameInput) {
      vendor = validVendors.find((v) => v.username.toLowerCase() === usernameInput.toLowerCase());
    } else if (selectedVendor) {
      vendor = validVendors.find((v) => v.id.toString() === selectedVendor);
    }

    if (vendor) {
      setSelectedVendor(vendor.username);
      toast.success(`Ahora estás viendo la cuenta de ${vendor!.username}`);
    } else {
      toast.error('Profesional no encontrado');
    }
  };

  const slides = Array.from({ length: 6 }, (_, index) => ({
    id: index + 1,
    title: `Página ${index + 1}`,
    url: `${FLASH_URL}/posts/${activeTab}?id=${index + 1}&vendor=${selectedVendor}`
  }));

  const handleTabClick = (tab: TabType) => {
    setActiveTab(tab);
  };

  if (isLoadingVendors) return <Loader fullHeight />;

  return (
    <main className="min-h-screen bg-gray-100 py-10 px-4">
      <div className="max-w-md mx-auto">
        <div className="flex flex-col py-8">
          <span className="mb-1">Selecciona el profesional a visualizar</span>
          <select value={selectedVendor} onChange={handleSelectChange} className="p-2 w-full border rounded">
            <option value="">Selecciona un professional</option>
            {sortedVendors.map((vendor) => (
              <option key={vendor.id} value={vendor.username}>
                {vendor.username}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-2 mt-2">
            <InputV2
              id="username"
              type="text"
              value={usernameInput}
              onChange={handleUsernameInputChange}
              placeholder="Ingresa el nombre de usuario"
            />
            <Button onClick={handleSubmit}>Ver cuenta</Button>
          </div>
        </div>
        <div
          role="tablist"
          className="border grid grid-cols-2 gap-0.5 text-gray-400 cursor-pointer rounded-full mb-4 shadow-md"
        >
          <div
            role="tab"
            className={classNames(
              'py-2 px-4 rounded-l-full flex items-center justify-center font-bold',
              activeTab === TabType.FEED && 'bg-blue text-white'
            )}
            onClick={() => handleTabClick(TabType.FEED)}
          >
            Feed
          </div>
          <div
            role="tab"
            className={classNames(
              'py-2 px-4 rounded-r-full flex items-center justify-center font-bold',
              activeTab === TabType.STORY && 'bg-blue text-white'
            )}
            onClick={() => handleTabClick(TabType.STORY)}
          >
            Stories
          </div>
        </div>
        <div className="space-y-8 p-4 max-w-2xl mx-auto">
          <Button
            isLoading={isLoading}
            onClick={() => downloadSlide(slides.map((slide) => slide.id))}
            variant="outline"
            className="w-full bg-white/80 hover:bg-white text-gray-700 border-gray-300 hover:border-gray-400 transition-all duration-200"
          >
            Descargar todo
          </Button>
          {slides.map((slide) => (
            <motion.div
              key={slide.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="overflow-hidden border-none duration-300">
                <CardContent className="!p-0">
                  <div className="bg-gray-100 p-4 flex items-center justify-between border">
                    <h3 className="text-base font-semibold text-gray-800">{slide.title}</h3>
                    <div className="flex gap-2">
                      <Button
                        isLoading={isLoading || downloadingSlides.includes(slide.id)}
                        disabled={isLoading}
                        variant="outline"
                        size="sm"
                        className="w-fit bg-white/80 hover:bg-white text-gray-700 border-gray-300 hover:border-gray-400 transition-all duration-200"
                        onClick={() => downloadSlide(slide.id)}
                      >
                        Descargar
                      </Button>
                      <Link to={slide.url} target="_blank">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-fit bg-white/80 hover:bg-white text-gray-700 border-gray-300 hover:border-gray-400 transition-all duration-200"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </main>
  );
}
